import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BookOpen, ArrowLeft, Award, Download, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "../utils/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StudentCertificates({ user }) {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [downloading, setDownloading] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    try {
      setLoading(true);
      const response = await authFetch('/certificates/students');
      
      if (response.ok) {
        const data = await response.json();
        setCertificates(data);
      } else {
        toast.error('Failed to load certificates');
      }
    } catch (error) {
      console.error('Error loading certificates:', error);
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (certificateId, studentName, moduleName) => {
    try {
      setDownloading(certificateId);
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${API}/certificates/${certificateId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `certificate_${studentName.replace(/\s+/g, '_')}_${moduleName.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Certificate downloaded');
      } else {
        toast.error('Failed to download certificate');
      }
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate');
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const filteredCertificates = certificates.filter(cert =>
    cert.student_name?.toLowerCase().includes(search.toLowerCase()) ||
    (cert.course_name || cert.module_name)?.toLowerCase().includes(search.toLowerCase()) ||
    cert.student_email?.toLowerCase().includes(search.toLowerCase()) ||
    cert.certificate_id?.toLowerCase().includes(search.toLowerCase())
  );

  // Group certificates by course for summary
  const moduleStats = certificates.reduce((acc, cert) => {
    const course = cert.course_name || cert.module_name || 'Unknown Course';
    if (!acc[course]) {
      acc[course] = 0;
    }
    acc[course]++;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAEDCD] flex items-center justify-center">
        <div className="text-[#3A5A40] text-lg">Loading certificates...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9] p-2 sm:p-3"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <div className="flex items-center gap-2 sm:gap-3">
            <Award className="w-6 h-6 sm:w-8 sm:h-8 text-[#3A5A40]" strokeWidth={1.5} />
            <h1 className="text-lg sm:text-2xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
              Student Certificates
            </h1>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-4 sm:p-6 md:p-12">
        {/* Stats Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#3A5A40]/10 rounded-full flex items-center justify-center">
                <Award className="w-5 h-5 text-[#3A5A40]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#344E41]">{certificates.length}</p>
                <p className="text-xs text-[#588157]">Total Certificates</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#3A5A40]/10 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-[#3A5A40]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#344E41]">
                  {new Set(certificates.map(c => c.user_id)).size}
                </p>
                <p className="text-xs text-[#588157]">Students with Certs</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-xl col-span-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#3A5A40]/10 rounded-full flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-[#3A5A40]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#344E41]">{Object.keys(moduleStats).length}</p>
                <p className="text-xs text-[#588157]">Courses Completed</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative w-full sm:max-w-xl">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A3B18A]" strokeWidth={1.5} />
            <Input
              type="text"
              placeholder="Search by student name, email, course, or certificate ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="certificate-search-input"
              className="pl-12 bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg placeholder:text-[#A3B18A]"
            />
          </div>
        </div>

        {/* Certificates List */}
        {filteredCertificates.length === 0 ? (
          <Card className="p-8 sm:p-12 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
            <Award className="w-12 h-12 sm:w-16 sm:h-16 text-[#A3B18A] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#588157]">
              {search ? "No certificates match your search" : "No student certificates yet"}
            </p>
            {!search && (
              <p className="text-sm text-[#A3B18A] mt-2">
                Certificates are generated when teachers mark students as completed
              </p>
            )}
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredCertificates.map((cert) => (
              <Card
                key={cert.certificate_id}
                data-testid={`cert-card-${cert.certificate_id}`}
                className="p-4 sm:p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <Avatar className="w-12 h-12 border-2 border-[#CCD5AE]">
                      <AvatarImage src={cert.student_picture} />
                      <AvatarFallback className="bg-[#3A5A40]/10 text-[#3A5A40] font-medium">
                        {getInitials(cert.student_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
                        {cert.student_name}
                      </h3>
                      <p className="text-sm text-[#588157]">{cert.student_email}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full sm:w-auto">
                    <div className="flex-1 sm:text-right">
                      <div className="flex items-center gap-2 sm:justify-end">
                        <Award className="w-4 h-4 text-[#D4A373]" strokeWidth={1.5} />
                        <span className="font-medium text-[#344E41]">{cert.course_name || cert.module_name}</span>
                      </div>
                      <p className="text-xs text-[#A3B18A]">
                        Issued: {formatDate(cert.issued_at)}
                      </p>
                      <p className="text-xs text-[#A3B18A] font-mono">
                        {cert.certificate_id}
                      </p>
                    </div>
                    
                    <Button
                      onClick={() => handleDownload(cert.certificate_id, cert.student_name, cert.course_name || cert.module_name)}
                      disabled={downloading === cert.certificate_id}
                      data-testid={`download-cert-${cert.certificate_id}`}
                      className="bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md active:scale-95 font-medium rounded-lg w-full sm:w-auto"
                    >
                      {downloading === cert.certificate_id ? (
                        'Downloading...'
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                          Download PDF
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
